import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { Share } from '@capacitor/share';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { Network } from '@capacitor/network';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

/**
 * Check if the app is running as a native mobile app
 */
export const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Get the current platform (ios, android, or web)
 */
export const getPlatform = () => {
  return Capacitor.getPlatform();
};

/**
 * Check if running on iOS
 */
export const isIOS = () => {
  return Capacitor.getPlatform() === 'ios';
};

/**
 * Check if running on Android
 */
export const isAndroid = () => {
  return Capacitor.getPlatform() === 'android';
};

/**
 * Initialize native app features
 * Note: Push notifications are NOT initialized here to prevent crashes
 * Call enablePushNotifications() explicitly when ready
 */
export const initializeApp = async () => {
  if (!isNativePlatform()) return;

  try {
    // Hide splash screen after app is ready
    if (Capacitor.isPluginAvailable('SplashScreen')) {
      await SplashScreen.hide();
    }

    // Set status bar style (wrapped in try-catch for safety)
    if (Capacitor.isPluginAvailable('StatusBar')) {
      try {
        if (isIOS()) {
          await StatusBar.setStyle({ style: Style.Light });
        } else {
          await StatusBar.setBackgroundColor({ color: '#ffffff' });
        }
      } catch (e) {
        console.warn('StatusBar setup failed:', e);
      }
    }

    // Check network status (non-blocking)
    if (Capacitor.isPluginAvailable('Network')) {
      try {
        const status = await Network.getStatus();
        console.log('Network status:', status);
      } catch (e) {
        console.warn('Network status check failed:', e);
      }
    }

  } catch (error) {
    console.error('Error initializing app:', error);
  }
};

/**
 * Enable push notifications - call this explicitly from settings/onboarding
 * This is separated from initializeApp to prevent crashes when Firebase is not configured
 */
export const enablePushNotifications = async (): Promise<boolean> => {
  if (!isNativePlatform()) return false;
  
  if (!Capacitor.isPluginAvailable('PushNotifications')) {
    console.warn('PushNotifications plugin not available');
    return false;
  }

  try {
    const result = await requestPushPermissions();
    return result;
  } catch (error) {
    console.error('Error enabling push notifications:', error);
    return false;
  }
};

/**
 * Take a photo with the camera
 */
export const takePhoto = async () => {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.Uri,
      source: CameraSource.Camera,
    });

    return image.webPath;
  } catch (error) {
    console.error('Error taking photo:', error);
    return null;
  }
};

/**
 * Pick an image from gallery
 */
export const pickImage = async () => {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.Uri,
      source: CameraSource.Photos,
    });

    return image.webPath;
  } catch (error) {
    console.error('Error picking image:', error);
    return null;
  }
};

/**
 * Get current position
 */
export const getCurrentPosition = async () => {
  try {
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 10000,
    });

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
    };
  } catch (error) {
    console.error('Error getting position:', error);
    return null;
  }
};

/**
 * Watch position changes
 */
export const watchPosition = (callback: (position: any) => void) => {
  return Geolocation.watchPosition(
    { enableHighAccuracy: true },
    (position, err) => {
      if (err) {
        console.error('Error watching position:', err);
        return;
      }
      if (position) {
        callback({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      }
    }
  );
};

/**
 * Share content
 */
export const shareContent = async (title: string, text: string, url?: string) => {
  try {
    await Share.share({
      title,
      text,
      url,
      dialogTitle: 'Share property',
    });
    return true;
  } catch (error) {
    console.error('Error sharing:', error);
    return false;
  }
};

/**
 * Request push notification permissions
 * Only call this after checking plugin availability
 */
export const requestPushPermissions = async () => {
  if (!isNativePlatform()) return false;
  
  if (!Capacitor.isPluginAvailable('PushNotifications')) {
    console.warn('PushNotifications plugin not available');
    return false;
  }

  try {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      console.warn('Push notification permission not granted');
      return false;
    }

    await PushNotifications.register();
    return true;
  } catch (error) {
    console.error('Error requesting push permissions:', error);
    return false;
  }
};

/**
 * Register push notification listeners
 */
export const registerPushListeners = (
  onRegistration: (token: string) => void,
  onNotification: (notification: any) => void
) => {
  if (!isNativePlatform()) return;

  // Registration success
  PushNotifications.addListener('registration', (token) => {
    console.log('Push registration success, token:', token.value);
    onRegistration(token.value);
  });

  // Registration error
  PushNotifications.addListener('registrationError', (error) => {
    console.error('Push registration error:', error);
  });

  // Notification received
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('Push notification received:', notification);
    onNotification(notification);
  });

  // Notification action performed
  PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
    console.log('Push notification action performed:', notification);
    onNotification(notification.notification);
  });
};

/**
 * Store data locally
 */
export const storeData = async (key: string, value: string) => {
  try {
    await Preferences.set({ key, value });
    return true;
  } catch (error) {
    console.error('Error storing data:', error);
    return false;
  }
};

/**
 * Retrieve stored data
 */
export const getData = async (key: string) => {
  try {
    const { value } = await Preferences.get({ key });
    return value;
  } catch (error) {
    console.error('Error getting data:', error);
    return null;
  }
};

/**
 * Remove stored data
 */
export const removeData = async (key: string) => {
  try {
    await Preferences.remove({ key });
    return true;
  } catch (error) {
    console.error('Error removing data:', error);
    return false;
  }
};

/**
 * Clear all stored data
 */
export const clearAllData = async () => {
  try {
    await Preferences.clear();
    return true;
  } catch (error) {
    console.error('Error clearing data:', error);
    return false;
  }
};

/**
 * Get network status
 */
export const getNetworkStatus = async () => {
  try {
    const status = await Network.getStatus();
    return {
      connected: status.connected,
      connectionType: status.connectionType,
    };
  } catch (error) {
    console.error('Error getting network status:', error);
    return { connected: true, connectionType: 'unknown' };
  }
};

/**
 * Listen to network status changes
 */
export const watchNetworkStatus = (callback: (status: any) => void) => {
  return Network.addListener('networkStatusChange', (status) => {
    callback({
      connected: status.connected,
      connectionType: status.connectionType,
    });
  });
};

/**
 * Trigger haptic feedback
 */
export const hapticImpact = async (style: 'LIGHT' | 'MEDIUM' | 'HEAVY' = 'MEDIUM') => {
  if (!isNativePlatform()) return;

  try {
    const impactStyle = {
      LIGHT: ImpactStyle.Light,
      MEDIUM: ImpactStyle.Medium,
      HEAVY: ImpactStyle.Heavy,
    }[style];

    await Haptics.impact({ style: impactStyle });
  } catch (error) {
    console.error('Error triggering haptic:', error);
  }
};

/**
 * Trigger haptic vibration
 */
export const hapticVibrate = async () => {
  if (!isNativePlatform()) return;

  try {
    await Haptics.vibrate({ duration: 100 });
  } catch (error) {
    console.error('Error triggering vibration:', error);
  }
};

/**
 * Trigger haptic notification
 */
export const hapticNotification = async (type: 'SUCCESS' | 'WARNING' | 'ERROR' = 'SUCCESS') => {
  if (!isNativePlatform()) return;

  try {
    // Android doesn't support notification type haptics, use vibrate instead
    if (isAndroid()) {
      await Haptics.vibrate({ duration: 50 });
      return;
    }

    // iOS supports notification types
    const notificationType = {
      SUCCESS: 'SUCCESS',
      WARNING: 'WARNING',
      ERROR: 'ERROR',
    }[type];

    await Haptics.notification({ type: notificationType as any });
  } catch (error) {
    console.error('Error triggering haptic notification:', error);
  }
};

/**
 * Check if feature is available
 */
export const isFeatureAvailable = (plugin: string) => {
  return Capacitor.isPluginAvailable(plugin);
};

export default {
  isNativePlatform,
  getPlatform,
  isIOS,
  isAndroid,
  initializeApp,
  enablePushNotifications,
  takePhoto,
  pickImage,
  getCurrentPosition,
  watchPosition,
  shareContent,
  requestPushPermissions,
  registerPushListeners,
  storeData,
  getData,
  removeData,
  clearAllData,
  getNetworkStatus,
  watchNetworkStatus,
  hapticImpact,
  hapticVibrate,
  hapticNotification,
  isFeatureAvailable,
};

